import { Box, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PsychAppointmentBookingSource, MatchingProfileData, MatchingProvidersType, GetMatchesInput, getMatches, patientMatchWithProvider, isApiError, PatientMatchWithProviderErrorOutput } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { L10nHTML, useL10n, useTabsSyncedCallbackTrigger } from '@uplift/hooks';
import { ConfirmDialog, ProviderList, SelectedProvider } from '@uplift/pages';
import { useInsuranceCompaniesQuery, useUserInsurancesQuery, useUserProfileQuery } from '@uplift/queries';
import { DateObject } from '@uplift/services';
import { ProviderMedicalRole } from '@uplift/types';
import { AutoLogoutTrigger, Button, ContextualNavigationLayout, ScrollableView, useAlert } from '@uplift/ui-v2';
import { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { useFindPsychiatristPageStyles } from './styles';

const seed = Math.random();
const PROVIDERS_PAGE_SIZE = 5;

export const FindPsychiatristPage: React.VFC<{ source?: PsychAppointmentBookingSource }> = ({ source }) => {
  const l10n = useL10n();
  const history = useHistory();
  const currentUser = useCurrentuser();
  const snackbar = useAlert();
  const queryClient = useQueryClient();
  const { classes } = useFindPsychiatristPageStyles();
  const logoutTrigger = useTabsSyncedCallbackTrigger(AutoLogoutTrigger.logoutManual);
  const [ selectedProvider, setSelectedProvider ] = useState<MatchingProfileData>(null);
  const [ appointmentTime, setAppointmentTime ] = useState<DateObject>(null);
  const [ providersToShow, setProvidersToShow ] = useState(PROVIDERS_PAGE_SIZE);
  const { mutateAsync: addAppointment, isLoading: isBooking } = useMutation(patientMatchWithProvider);
  const { data: profile, isLoading: profileLoading } = useUserProfileQuery(currentUser.id, { enabled: !!currentUser.id });
  const { data: insurances, isLoading: insuranceLoading } = useUserInsurancesQuery(currentUser.id, { enabled: !!currentUser.id });
  const { data: companies = [] } = useInsuranceCompaniesQuery();
  const profileDataLoading = profileLoading || insuranceLoading;
  const patientStateId = profile?.address?.stateId;
  const patientInsuranceCompanyId = insurances?.[0]?.insurance?.companyId;
  const matchingQueryInput: GetMatchesInput = useMemo(() => ({
    patientId: currentUser.id,
    seed: seed,
    stateId: patientStateId,
    insuranceCompanyId: patientInsuranceCompanyId,
    medicalRole: ProviderMedicalRole.psychiatrist,
  }), [ currentUser.id, patientInsuranceCompanyId, patientStateId ]);

  const { data: providers, isInitialLoading: providersLoading } = useQuery({
    queryKey: [ 'get-providers', matchingQueryInput ],
    queryFn: () => getMatches(matchingQueryInput),
    enabled: !profileDataLoading,
  });

  const matchingProfiles = providers?.data.matchingProfiles ?? [];
  const matchingPaymentType = providers?.data.type;
  const isLoading = profileDataLoading || providersLoading;

  const matchedInsuranceCompany = matchingPaymentType === MatchingProvidersType.cashpay
    ? null
    : companies.find(({ id }) => id === patientInsuranceCompanyId) ?? null;

  const handleBack = useCallback(() => {
    history.goBack();
  }, [ history ]);

  const handleLogout = useCallback(() => {
    logoutTrigger();
  }, [ logoutTrigger ]);

  const handleScheduleSession = useCallback((selectedSlot: DateObject) => {
    setAppointmentTime(selectedSlot);
  }, []);

  const handleDialogClose = useCallback(async (confirm?: boolean) => {
    if (!confirm) {
      setAppointmentTime(null);

      return;
    }

    try {
      const result = await addAppointment({
        providerId: selectedProvider.user.id,
        fromDatetime: appointmentTime.format(),
        psychiatryBookingSource: source,
      });

      if (result?.data?.created) {
        history.push('/main');
        snackbar.success('Consultation Scheduled');
      } else {
        snackbar.error(l10n.schedule.createAppointment.providerErrors.unknown.body);
      }
    } catch (err) {
      if (isApiError<PatientMatchWithProviderErrorOutput>(err)) {
        const errorMapping = l10n.schedule.createAppointment.errors;
        const errorKey = err.response?.data?.error;
        const mappedError = errorMapping[errorKey];

        if (mappedError) {
          await queryClient.invalidateQueries({ queryKey: [ 'availability-slots', selectedProvider.user.id ] });
          setAppointmentTime(null);
          snackbar.error(mappedError.body, {
            title: mappedError.title
          });
        } else {
          snackbar.error(l10n.schedule.createAppointment.providerErrors.unknown.body);
        }
      }
      throw err;
    }
  }, [ addAppointment, appointmentTime, history, queryClient, selectedProvider?.user?.id, snackbar, source, l10n ]);

  const headerProps = useMemo(() => {
    const logout = {
      onRightButtonClick: handleLogout,
      rightButtonText: l10n.navigation.logout,
    };

    if (selectedProvider) {
      return {
        ...logout,
        leftButton: (
          <Button startIcon={<ArrowBack />} onClick={() => setSelectedProvider(null)}>
            {l10n.navigation.back}
          </Button>
        ),
      };
    }

    return {
      ...logout,
      onLeftButtonClick: handleBack,
      leftButtonText: l10n.navigation.back,
    };
  }, [ handleBack, handleLogout, l10n.navigation.back, l10n.navigation.logout, selectedProvider ]);

  if (selectedProvider) {
    return (
      <ContextualNavigationLayout headerProps={headerProps} fullWidth>
        <ScrollableView>
          <SelectedProvider
            selectedProvider={selectedProvider.user.id}
            stateId={patientStateId}
            medicalRole={ProviderMedicalRole.psychiatrist}
            matchedInsuranceCompany={matchedInsuranceCompany}
            onScheduleSession={handleScheduleSession}
            isScrollableView
          />
        </ScrollableView>
        {appointmentTime && (
          <ConfirmDialog
            provider={selectedProvider}
            appointmentTime={appointmentTime}
            isLoading={isBooking}
            onClose={handleDialogClose}
          />
        )}
      </ContextualNavigationLayout>
    );
  }

  const hasProviders = matchingProfiles.length > 0;

  return (
    <ContextualNavigationLayout headerProps={headerProps} fullWidth>
      <ScrollableView className={classes.bg}>
        <Box className={classes.root}>
          {!isLoading && hasProviders && (
            <Box className={classes.header}>
              <Typography variant="h1">
                {l10n.findPsychiatrist.found(matchingProfiles.length)}{' '}
                <Typography variant="inherit" component="span" color="secondary">
                  {l10n.findPsychiatrist.forYou}
                </Typography>
              </Typography>
            </Box>
          )}
          {!isLoading && !hasProviders
            ? (
              <Box className={classes.emptyState}>
                <Typography>{l10n.findPsychiatrist.emptyStateAllProviders}</Typography>
                <Box mt={1}>
                  <L10nHTML>{l10n.findPsychiatrist.emptyStateLine2}</L10nHTML>
                </Box>
              </Box>
            )
            : (
              <ProviderList
                isLoading={isLoading}
                stateId={patientStateId}
                medicalRole={ProviderMedicalRole.psychiatrist}
                matchingProfiles={matchingProfiles}
                insuranceCompanyId={patientInsuranceCompanyId}
                matchingPaymentType={matchingPaymentType}
                noInsuranceProviders={providers?.data.noInsuranceProviders}
                emptyListRelaxed={providers?.data.emptyListRelaxed}
                providersToShow={providersToShow}
                onLoadMore={() => setProvidersToShow(matchingProfiles.length)}
                onViewClick={setSelectedProvider}
              />
            )}
        </Box>
      </ScrollableView>
    </ContextualNavigationLayout>
  );
};
